import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchError } from "./commonSlice";

const initialState = {
  list: [],
  perPage: "",
  total: 0,
  loading: false,
  settings: [],
  settingsLoading: false,
  detail: null,
  recipients: [],
  detailLoading: false,
};

export const getSupplierNotificationList = createAsyncThunk(
  "get-supplier-notification",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("supplier-notification/list", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

export const sendSupplierNotification = createAsyncThunk(
  "send-supplier-notification",
  async (data, { dispatch }) => {
    try {
      const response = await axios.post("supplier-notification", data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Previews how many vendors an audience resolves to, before anything is sent.
export const getNotificationAudienceCount = createAsyncThunk(
  "get-notification-audience-count",
  async (params, { dispatch }) => {
    try {
      const response = await axios.get("supplier-notification/audience-count", {
        params,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

export const getSupplierNotificationById = createAsyncThunk(
  "get-supplier-notification-by-id",
  async (id, { dispatch }) => {
    try {
      const response = await axios.get(`supplier-notification/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

export const deleteSupplierNotification = createAsyncThunk(
  "delete-supplier-notification",
  async (id, { dispatch }) => {
    try {
      const response = await axios.delete(`supplier-notification/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

export const getNotificationSettings = createAsyncThunk(
  "get-notification-settings",
  async (_, { dispatch }) => {
    try {
      const response = await axios.get("supplier-notification/settings");
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

export const updateNotificationSettings = createAsyncThunk(
  "update-notification-settings",
  async (data, { dispatch }) => {
    try {
      const response = await axios.put("supplier-notification/settings", data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

const supplierNotificationSlice = createSlice({
  name: "supplierNotification",
  initialState,
  reducers: {
    clearNotificationDetail: (state) => {
      state.detail = null;
      state.recipients = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSupplierNotificationList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSupplierNotificationList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action?.payload?.data?.data?.notificationList ?? [];
        state.perPage = action?.payload?.data?.data?.perPage ?? 1;
        state.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getSupplierNotificationList.rejected, (state) => {
        state.loading = false;
        state.list = [];
        state.perPage = "";
        state.total = 0;
      })
      .addCase(getNotificationSettings.pending, (state) => {
        state.settingsLoading = true;
      })
      .addCase(getNotificationSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action?.payload?.data?.data?.settings ?? [];
      })
      .addCase(getNotificationSettings.rejected, (state) => {
        state.settingsLoading = false;
        state.settings = [];
      })
      .addCase(getSupplierNotificationById.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(getSupplierNotificationById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action?.payload?.data?.data?.notification ?? null;
        state.recipients = action?.payload?.data?.data?.recipients ?? [];
      })
      .addCase(getSupplierNotificationById.rejected, (state) => {
        state.detailLoading = false;
        state.detail = null;
        state.recipients = [];
      });
  },
});

export const { clearNotificationDetail } = supplierNotificationSlice.actions;

export default supplierNotificationSlice.reducer;
